import React from 'react';
import {
    MoneyHand24Regular,
    QuestionCircle16Regular,
    Location16Regular,
    Video16Regular,
    Tag16Regular,
    ReOrderDotsVertical20Regular,
} from "@fluentui/react-icons";
import { ContractStage } from "../../../../models/Entities/Contracts/ContractStage";
import { ConfirmationButtonComponent } from "../../../../commons/component/ConfirmationButton/ConfirmationButtonComponent";
import { ErrorType } from "../../../../models/HttpError";
import { useTranslation } from 'react-i18next';
import StageItemLinkComponent from '../StageItems/StageItemLink/StageItemLink.Component';
import './StageList.Styles.css';

export interface IProps {
    Stage: ContractStage;
    ProtocolId: number | undefined;
    index: number;
    isDeleting: boolean;
    failOnDeleting: boolean;
    error: ErrorType | undefined;
    onEdit?: (stage: ContractStage) => void;
    onDelete?: (stage: ContractStage) => void;
    // drag & drop
    isDragging?: boolean;
    isDragOver?: boolean;
    onDragStart?: (e: React.DragEvent<HTMLDivElement>) => void;
    onDragOverCard?: (e: React.DragEvent<HTMLDivElement>) => void;
    onDrop?: (e: React.DragEvent<HTMLDivElement>) => void;
    onDragEnd?: (e: React.DragEvent<HTMLDivElement>) => void;
}


export const StageItemComponent = (props: IProps) => {
    const [t] = useTranslation();
    const s = props.Stage;

    const cardClassName = [
        'si-card',
        props.isDragging  ? 'si-card--dragging'  : '',
        props.isDragOver  ? 'si-card--drag-over'  : '',
    ].filter(Boolean).join(' ');

    return (
        <div
            className={cardClassName}
            role="button"
            tabIndex={0}
            draggable
            onClick={() => props.onEdit?.(s)}
            onKeyDown={(e) => e.key === 'Enter' && props.onEdit?.(s)}
            onDragStart={props.onDragStart}
            onDragOver={props.onDragOverCard}
            onDrop={props.onDrop}
            onDragEnd={props.onDragEnd}
        >

            {/* ── DRAG HANDLE ── */}
            <div
                className="si-drag-handle"
                onClick={(e) => e.stopPropagation()}
                title={t('stageItem.props.dragToReorder', 'Arrastrar para reordenar')}
            >
                <ReOrderDotsVertical20Regular />
            </div>

            {/* ── ORDER ── */}
            <div className="si-order">
                <span className="si-order-num">{s.stageOrder ?? props.index + 1}</span>
                <span className="si-order-label">{t('stageItem.props.visit')}</span>
            </div>

            {/* ── BODY ── */}
            <div className="si-body">

                {/* Row 1: name + cycle */}
                <div className="si-title-row">
                    <span className="si-name">{s.stageName}</span>
                    {s.cycle && (
                        <span className="si-cycle">{s.cycle.cycleName}</span>
                    )}
                </div>

                {/* Row 2: comments */}
                {s.comments && (
                    <p className="si-desc">{s.comments}</p>
                )}

                {/* Row 3: execution mode + stage type */}
                <div className="si-meta-row">
                    {s.isOnSiteMandatory ? (
                        <span className="si-meta-pill si-meta-pill--inperson"><Location16Regular />{t('stageItem.props.inPerson')}</span>
                    ) : (
                        <span className="si-meta-pill si-meta-pill--remote"><Video16Regular />{t('stageItem.props.remote')}</span>
                    )}
                    {s.contractStageType && (
                        <>
                            <span className="si-meta-divider">·</span>
                            <span className="si-meta-pill si-meta-pill--type"><Tag16Regular />{s.contractStageType.name}</span>
                        </>
                    )}
                </div>

                {/* Row 4: feature badges */}
                {(s.markAsPayable || s.isOptional) && (
                    <div className="si-badges">
                        {s.markAsPayable && (
                            <span className="si-badge si-badge--payable">
                                <MoneyHand24Regular />{t('stageItem.props.payable', 'Liquidable')}
                            </span>
                        )}
                        {s.isOptional && (
                            <span className="si-badge si-badge--optional">
                                <QuestionCircle16Regular />{t('stageItem.props.optional')}
                            </span>
                        )}
                    </div>
                )}

            </div>

            {/* ── ASIDE: link + delete ── */}
            <div
                className="si-aside"
                data-stop-stage-edit="true"
                onClick={(e) => e.stopPropagation()}
            >
                <StageItemLinkComponent
                    ContractStage={s}
                    ProtocolId={props.ProtocolId}
                />
                <ConfirmationButtonComponent
                    ButtonId={"deletestage" + s.id}
                    ButtonIconName={"Delete"}
                    ButtonText={""}
                    ButtonCssClass={""}
                    ConfirmationButtonText={t('stageItem.confirmationButton.deleteButton')}
                    ConfirmationButtonCssClass={""}
                    CancelButtonText={t('stageItem.confirmationButton.cancelButton')}
                    CancelButtonCssClass={""}
                    Title={t('stageItem.confirmationButton.deleteVisit')}
                    Description={""}
                    ElementTextHeader={t('stageItem.confirmationButton.deleteVisitInformation') + s.stageName}
                    ElementTextInformation={t('stageItem.confirmationButton.operationMessage')}
                    onConfirmation={() => props.onDelete?.(s)}
                    onCancel={() => {}}
                    isOperationExecuting={props.isDeleting}
                    isOperationExecutedWithError={props.failOnDeleting}
                    OperationError={props.error}
                />
            </div>

        </div>
    );
};